import React, { useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import {
  Box,
  VStack,
  Heading,
  SimpleGrid,
  Flex,
  IconButton,
  Text,
  Input,
  createListCollection
} from '@chakra-ui/react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { CustomInput } from '../../custom/CustomInput';
import { CustomButton } from '../../custom/CustomButton';
import { Field } from '../../custom/CustomField';
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '../../ui/select';

const BankAccounts = ({ onNext, onBack, loading, initialValues }) => {
  const [currencySearch, setCurrencySearch] = useState("");
  
  const currencyList = [
      "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "CNY", "AED", "INR", "SGD", "HKD", "SAR", "QAR", "KWD"
  ];

  const filteredCurrencies = currencyList.filter(c => c.toLowerCase().includes(currencySearch.toLowerCase()));
  
  const currencyCollection = createListCollection({
      items: filteredCurrencies.map(c => ({ label: c, value: c }))
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: initialValues || {
      bankAccounts: [
        {
          bankName: '',
          accountName: '',
          accountNumber: '',
          swiftCode: '',
          iban: '',
          bankAddress: '',
          currency: '',
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'bankAccounts',
  });

  const onSubmit = (data) => {
    onNext(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={8} align="stretch">
        <Heading size="md" color="blue.600">
          Bank Accounts
        </Heading>
        <Text color="gray.600">
          Please provide your corporate bank account details.
        </Text>

        {fields.map((field, index) => (
          <Box
            key={field.id}
            p={6}
            borderWidth="1px"
            borderRadius="md"
            position="relative"
            bg="gray.50"
          >
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="sm">Bank Account #{index + 1}</Heading>
              {fields.length > 1 && (
                <IconButton
                  colorPalette="red"
                  variant="ghost"
                  onClick={() => remove(index)}
                  aria-label="Remove bank account"
                >
                  <FaTrash />
                </IconButton>
              )}
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} columnGap={8}>
              <CustomInput
                label="Bank Name"
                error={errors.bankAccounts?.[index]?.bankName}
                required
                placeholder="Enter bank name"
                {...register(`bankAccounts.${index}.bankName`, { required: 'Required' })}
              />

              <CustomInput
                label="Account Name"
                error={errors.bankAccounts?.[index]?.accountName}
                required
                placeholder="Enter account holder name"
                {...register(`bankAccounts.${index}.accountName`, { required: 'Required' })}
              />

              <CustomInput
                label="Account Number"
                error={errors.bankAccounts?.[index]?.accountNumber}
                required
                placeholder="Enter account number"
                {...register(`bankAccounts.${index}.accountNumber`, { required: 'Required' })}
              />

              <CustomInput
                label="SWIFT / BIC Code"
                error={errors.bankAccounts?.[index]?.swiftCode}
                required
                placeholder="Enter SWIFT/BIC code"
                {...register(`bankAccounts.${index}.swiftCode`, { 
                  required: 'Required',
                  onChange: (e) => {
                    e.target.value = e.target.value.toUpperCase();
                  }
                })}
              />

              <CustomInput
                label="IBAN"
                error={errors.bankAccounts?.[index]?.iban}
                required
                placeholder="Enter IBAN"
                {...register(`bankAccounts.${index}.iban`, { 
                  required: 'Required',
                  onChange: (e) => {
                    e.target.value = e.target.value.toUpperCase();
                  }
                })}
              />

              <Controller
                  control={control}
                  name={`bankAccounts.${index}.currency`}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                     <Field 
                        label="Currency" 
                        invalid={!!errors.bankAccounts?.[index]?.currency} 
                        errorText={errors.bankAccounts?.[index]?.currency?.message} 
                        required 
                        width="100%" 
                        mb={4}
                     >
                        <SelectRoot 
                          collection={currencyCollection}
                          value={[field.value]} 
                          onValueChange={(e) => field.onChange(e.value[0])}
                          size="md"
                        >
                           <SelectTrigger
                              padding="12px 16px"
                              borderRadius="8px"
                              borderColor={!!errors.bankAccounts?.[index]?.currency ? "red.500" : "gray.300"}
                              _hover={{ borderColor: "blue.400" }}
                              _focus={{ 
                                borderColor: "blue.500", 
                                boxShadow: "0 0 0 1px #3182ce",
                                outline: "none"
                              }}
                              bg="white"
                              fontSize="md"
                              height="auto"
                              width="100%"
                              minHeight="48px" // Match CustomInput height
                           >
                              <SelectValueText placeholder="Select currency" />
                           </SelectTrigger>
                           <SelectContent zIndex={2000}>
                               <Box p={2} position="sticky" top="0" bg="white" zIndex="1">
                                  <Input 
                                      placeholder="Search currency..." 
                                      value={currencySearch} 
                                      onChange={(e) => setCurrencySearch(e.target.value)}
                                      onKeyDown={(e) => e.stopPropagation()} 
                                      size="sm"
                                  />
                               </Box>
                               {currencyCollection.items.map(item => (
                                   <SelectItem item={item} key={item.value}>
                                       {item.label}
                                   </SelectItem>
                               ))}
                           </SelectContent>
                        </SelectRoot>
                     </Field>
                  )}
              />

              <Box gridColumn={{ md: 'span 2' }}>
                  <CustomInput
                    label="Bank Address"
                    error={errors.bankAccounts?.[index]?.bankAddress}
                    required
                    placeholder="Enter full bank address"
                    {...register(`bankAccounts.${index}.bankAddress`, { required: 'Required' })}
                  />
              </Box>
            </SimpleGrid>
          </Box>
        ))}

        <CustomButton
          onClick={() =>
            append({
              bankName: '',
              accountName: '',
              accountNumber: '',
              swiftCode: '',
              iban: '',
              bankAddress: '',
              currency: '',
            })
          }
          variant="outline"
          colorScheme="blue"
        >
          <FaPlus style={{ marginRight: '8px' }} />
          Add Another Bank Account
        </CustomButton>

        <Flex justify="space-between" mt={8}>
          <CustomButton onClick={onBack} variant="ghost" color="gray.600">
            Back
          </CustomButton>
          <CustomButton
            type="submit"
            colorScheme="green"
            size="lg"
            isLoading={loading}
            loadingText="Submitting..."
          >
            Submit Application
          </CustomButton>
        </Flex>
      </VStack>
    </form>
  );
};

export default BankAccounts;
